/* global t, runtime, mergeState, safeArray, num */
// LÅN OG GJELD — lesemodell for byråets låneskjerm
//
// Området har ett spørsmål saldoen ikke kan svare på alene: hva består gjelden
// AV, og hvor mye av den kan långiveren bekrefte. To tall som stemmer sier
// ingenting når det ene av dem aldri er kontrollert mot en låneoppgave.
//
// Modellen skiller derfor tre ting som en vanlig differanse blander sammen:
//
//   A. Avstemming av dokumenterte lån — bokført saldo mot långiverens saldo,
//      for de lånene der långiverens saldo faktisk finnes for perioden.
//   B. Bokført saldo som ikke er verifisert — gjeld ingen ekstern kilde har
//      bekreftet. Den er ikke «avstemt til null», den er ikke avstemt.
//   C. Andre åpne forhold — påløpte renter, gebyrer og klassifisering. De er
//      manglende bokføringer, ikke uenighet om saldoen, og skal ikke gjøres om
//      til en differanse mot långiveren.
//
// Kildene er appens egne:
//   · `settings.loans` — låneregisteret, samme register `settings-loans.jsx`
//     fører og `businessLoanRegister.js` speiler. Ingen egen låneliste her.
//   · avstemmingskontoene — bokført saldo, inngående saldo og bevegelse fra
//     saldobalansen, og `loanEntries` med långiverens saldo for perioden.
//   · `buildLedgerLines` — posteringene bak bevegelsen.
//
// Ingen mockdata. Har foretaket ingen gjeld og ingen registrerte lån, er
// listene tomme og flaten sier det.
//
// Det som IKKE er portert fra designet, fordi appen ikke har kilden:
//   · «Avdrag betalt, men ikke bokført» som eget tall — en betaling appen ikke
//     har bokført finnes ikke i noen av kildene. Den fanges av at bokført saldo
//     avviker fra långiverens, og hører hjemme som en avstemmingspost.
//   · Automatisk kobling av dokument til lån ut fra filnavn. Koblingen settes
//     der den kan kontrolleres: på lånets egen dokumentasjonsrad.

// Dokumentasjonsstatusene, med designets egne ord. Statusen UTLEDES av
// saldodatoen mot periodeslutt — den lagres ikke, fordi en lagret status ville
// stått igjen som gyldig dagen etter at perioden flyttet seg.
const LOAN_DOC_STATUS = {
  gyldig_for_perioden: { get label() { return t('rcn.docStatus.validForPeriod', 'Gyldig for perioden'); }, cls: 'success' },
  fremfort: { get label() { return t('rcn.docStatus.carriedForward', 'Fremført til periodeslutt'); }, cls: 'success' },
  krever_fremforing: { get label() { return t('rcn.docStatus.needsCarryForward', 'Krever fremføring'); }, cls: 'warning' },
  feil_periode: { get label() { return t('rcn.docStatus.wrongPeriod', 'Feil periode'); }, cls: 'warning' },
  ufullstendig: { get label() { return t('rcn.docStatus.incomplete', 'Ufullstendig dokumentasjon'); }, cls: 'warning' },
  feil_selskap: { get label() { return t('rcn.docStatus.wrongCompany', 'Feil selskap'); }, cls: 'error' },
  mangler: { get label() { return t('rcn.docStatus.missing', 'Mangler'); }, cls: 'error' },
};

const LOAN_VERIFIED_DOC_STATUSES = ['gyldig_for_perioden', 'fremfort'];

// Vurderingene byrået kan lagre om selve dokumentet. Resten leses av datoen.
const LOAN_DOC_ISSUES = [
  { key: '', get label() { return t('rcn.docIssue.none', 'Ingen innvending'); } },
  { key: 'feil_selskap', get label() { return t('rcn.docIssue.wrongCompany', 'Gjelder feil selskap'); } },
  { key: 'ufullstendig', get label() { return t('rcn.docIssue.incomplete', 'Ufullstendig dokumentasjon'); } },
];

// Dokumenttypene låneområdet faktisk ber om. Fri tekst i dokumentmodellen —
// dette er forslagene, ikke en tvangstrøye.
const LOAN_DOCUMENT_KINDS = [
  'Låneoppgave',
  'Årsoppgave fra långiver',
  'Nedbetalingsplan',
  'Kontoutskrift',
  'Låneavtale',
  'Rentenota',
  'Saldobekreftelse',
  'Annet',
];

// Klassifiseringene en avstemmingspost på låneområdet kan ha. De ligger på
// `explainedItems`, samme modell som resten av avstemmingen bruker — et eget
// postregister for lån ville vært et andre sted å forklare en differanse.
const LOAN_ITEM_CLASSES = [
  'Påløpt rente',
  'Ikke bokført avdrag',
  'Ikke bokført gebyr',
  'Tidsavvik',
  'Valutadifferanse',
  'Feil hovedbokskonto',
  'Feil kortsiktig/langsiktig klassifisering',
  'Manglende dokumentasjon',
  'Annet',
];

// Nedbetalingstypene, med designets egne ord. Speiler `LOAN_REPAYMENT_TYPES` i
// functions/accounting/businessLoanRegister.ts — typen avgjør om planen kan
// regnes ut i det hele tatt.
const LOAN_REPAYMENT_TYPE_OPTIONS = [
  { key: 'serial', get label() { return t('rcn.repaymentType.serial', 'Serielån — like store avdrag'); } },
  { key: 'annuity', get label() { return t('rcn.repaymentType.annuity', 'Annuitetslån — like store terminbeløp'); } },
  { key: 'interest_only', get label() { return t('rcn.repaymentType.interestOnly', 'Avdragsfritt — bare renter'); } },
  { key: 'unknown', get label() { return t('rcn.repaymentType.unknown', 'Ukjent / annet'); } },
];

const loanRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
const loanMoneyOrNull = (value) => (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
  ? null
  : loanRound(value));
const loanIsDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());

// Fremføringen speiler `loanCarryForwardBalance` i
// functions/reconciliation/reconciliationLoanEntries.ts. Avdrag reduserer
// gjelden; nye trekk, renter og gebyrer øker den.
const loanCarryForwardBalance = (carry) => {
  if (!carry || typeof carry !== 'object') return null;
  const base = loanMoneyOrNull(carry.baseBalance);
  if (base === null) return null;
  return loanRound(base - num(carry.installments) + num(carry.drawdowns)
    + num(carry.interestAndFees) + num(carry.other));
};

// Speiler `loanDocumentationStatus` i backend. Rekkefølgen er den samme: en
// lagret innvending slår datoen, fordi et dokument som gjelder feil selskap
// ikke blir gyldig av å ha riktig dato.
const loanDocumentationStatus = (entry = {}, periodTo = null) => {
  if (entry.docIssue === 'feil_selskap') return 'feil_selskap';
  if (entry.docIssue === 'ufullstendig') return 'ufullstendig';
  const balance = loanMoneyOrNull(entry.documentedBalance);
  if (balance === null) return 'mangler';
  if (loanCarryForwardBalance(entry.carryForward) !== null) return 'fremfort';
  if (!loanIsDate(entry.documentedDate) || !loanIsDate(periodTo)) return 'ufullstendig';
  if (entry.documentedDate > periodTo) return 'feil_periode';
  if (entry.documentedDate < periodTo) return 'krever_fremforing';
  return 'gyldig_for_perioden';
};

// Kortsiktig eller langsiktig etter hovedbokskontoen. Grensene er de SAMME som
// balanseoppstillingen bruker (`backend-annual-accounts.jsx`: 2100–2299
// langsiktig, 2300–2999 kortsiktig) — to steder å avgjøre hvor en gjeldskonto
// hører hjemme ville gitt to balanser.
//
// Regelen er norsk kontoplan. Utenfor Norge returneres `null`, og flaten sier at
// klassifiseringen ikke kan leses av kontonummeret i stedet for å påstå noe om
// en kontoplan appen ikke kjenner.
const loanAccountClass = (code, country) => {
  if (String(country || 'NO').toUpperCase() !== 'NO') return null;
  const number = Number(String(code || '').trim());
  if (!Number.isFinite(number)) return null;
  if (number >= 2100 && number <= 2299) return 'langsiktig';
  if (number >= 2300 && number <= 2999) return 'kortsiktig';
  return null;
};

// Perioden regnes ut her og ikke leses av en sidefil: en tjeneste som er
// avhengig av at en side er lastet, gir tomme tall i test og i alt annet som
// ikke er avstemmingsmappa.
const loanPeriodRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    from: year + '-' + pad(monthIndex + 1) + '-01',
    to: year + '-' + pad(monthIndex + 1) + '-' + pad(lastDay),
  };
};

// Lånene i registeret, satt sammen med kontoen de ligger på og med långiverens
// saldo for denne perioden.
const loanBuildRows = ({ loans, accountsByCode, entries, periodTo, country }) => loans.map((loan) => {
  const account = accountsByCode.get(String(loan.ledgerAccountCode));
  const entry = entries[loan.id] || {};
  const docStatus = loanDocumentationStatus(entry, periodTo);
  const verified = LOAN_VERIFIED_DOC_STATUSES.includes(docStatus);
  const documentedBalance = loanCarryForwardBalance(entry.carryForward);
  const documented = documentedBalance === null ? loanMoneyOrNull(entry.documentedBalance) : documentedBalance;
  // Bokført gjeld står i kredit og altså med minus i hovedboken. Långiverens
  // saldo oppgis som et positivt skyldig beløp. Sammenlikningen skjer derfor på
  // absoluttverdien — uten den ville «differansen» vært to ganger saldoen.
  const booked = account ? Math.abs(loanRound(account.bookedBalance)) : null;
  const nextTwelve = window.avstLoanNextTwelveMonths
    ? window.avstLoanNextTwelveMonths(loan, periodTo)
    : null;
  return {
    ...loan,
    accountId: account ? account.accountId : null,
    accountName: account ? account.accountName || '' : '',
    accountClass: loanAccountClass(loan.ledgerAccountCode, country),
    // Flere lån kan dele én hovedbokskonto. Da er kontoens saldo ikke ETT låns
    // bokførte saldo, og differansen per lån kan ikke regnes ut.
    sharesAccount: false,
    bookedBalance: booked,
    documentedBalance: documented,
    documentedDate: loanIsDate(entry.documentedDate) ? entry.documentedDate : null,
    documentId: entry.documentId || null,
    documentLender: entry.documentLender || '',
    carryForward: entry.carryForward || null,
    docStatus,
    docIssue: entry.docIssue || '',
    verified,
    accruedInterest: loanMoneyOrNull(entry.accruedInterest),
    fees: loanMoneyOrNull(entry.fees),
    note: entry.note || '',
    nextTwelveMonths: nextTwelve,
    needsPlan: nextTwelve === null,
    planRemaining: loan.installmentAmount && loan.remainingTerms
      ? loanRound(num(loan.installmentAmount) * num(loan.remainingTerms))
      : null,
    difference: booked === null || documented === null || !verified ? null : loanRound(booked - documented),
  };
});

const loanMarkSharedAccounts = (rows) => {
  const perCode = new Map();
  for (const row of rows) {
    const code = String(row.ledgerAccountCode);
    perCode.set(code, (perCode.get(code) || 0) + 1);
  }
  for (const row of rows) {
    if (perCode.get(String(row.ledgerAccountCode)) > 1) {
      row.sharesAccount = true;
      row.bookedBalance = null;
      row.difference = null;
    }
  }
  return rows;
};

const buildLoanReconciliationView = ({ periodId, accounts = [], state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const range = loanPeriodRange(periodId);
  const periodTo = range ? range.to : null;
  const country = String(state.settings?.country || 'NO').toUpperCase();

  const accountRows = safeArray(accounts).map((account) => ({
    accountId: String(account.accountId),
    accountName: account.accountName || '',
    bookedBalance: loanRound(account.bookedBalance),
    openingBalance: loanRound(account.openingBalance),
    movement: loanRound(account.movement),
    accountClass: loanAccountClass(account.accountId, country),
    status: account.status || 'ikke_kontrollert',
  }));
  const accountsByCode = new Map(safeArray(accounts).map((account) => [String(account.accountId), account]));

  // Lånenes dokumentasjon ligger på områdets avstemmingskontoer. De samles her,
  // slik at et lån ikke får to sett dokumentasjon avhengig av hvilken konto det
  // ble åpnet fra.
  const entries = {};
  for (const account of safeArray(accounts)) {
    for (const [loanId, entry] of Object.entries(account.loanEntries || {})) entries[loanId] = entry;
  }

  const loans = safeArray(state.settings?.loans)
    .filter((loan) => loan && loan.id && String(loan.ledgerAccountCode || '').trim());
  const rows = loanMarkSharedAccounts(
    loanBuildRows({ loans, accountsByCode, entries, periodTo, country }),
  );

  const registeredCodes = new Set(rows.map((row) => String(row.ledgerAccountCode)));
  // Gjeldskontoer med saldo som ingen har registrert et lån for. Det er nettopp
  // dem avstemmingen skal fange: en gjeld ingen har lagt en plan for, og som
  // derfor ikke kan klassifiseres.
  const unregistered = accountRows.filter((account) => !registeredCodes.has(account.accountId)
    && Math.abs(account.bookedBalance) > 0.005);

  const verifiedRows = rows.filter((row) => row.verified && row.bookedBalance !== null && row.documentedBalance !== null);
  const verifiedBooked = loanRound(verifiedRows.reduce((sum, row) => sum + row.bookedBalance, 0));
  const verifiedDocumented = loanRound(verifiedRows.reduce((sum, row) => sum + row.documentedBalance, 0));
  const preliminaryDifference = loanRound(verifiedBooked - verifiedDocumented);

  // Forklarte poster er avstemmingspostene på områdets kontoer — samme modell
  // som resten av avstemmingen. En post uten begrunnelse forklarer ingenting,
  // og telles derfor ikke.
  const explained = loanRound(safeArray(accounts).reduce((sum, account) => sum
    + safeArray(account.explainedItems)
      .filter((item) => String(item?.reason || '').trim() && item?.resolved !== true)
      .reduce((inner, item) => inner + num(item.amount), 0), 0));
  const unexplainedDifference = loanRound(preliminaryDifference - explained);

  const bookedTotal = loanRound(accountRows.reduce((sum, account) => sum + Math.abs(account.bookedBalance), 0));
  const openingTotal = loanRound(accountRows.reduce((sum, account) => sum + Math.abs(account.openingBalance), 0));
  // Gjeld ingen ekstern kilde har bekreftet. Den er ikke en differanse — den er
  // fravær av grunnlag, og skal telles for seg.
  const unverifiedBooked = loanRound(Math.max(0, bookedTotal - verifiedBooked));

  const bookedShortTerm = loanRound(accountRows
    .filter((account) => account.accountClass === 'kortsiktig')
    .reduce((sum, account) => sum + Math.abs(account.bookedBalance), 0));
  const bookedLongTerm = loanRound(accountRows
    .filter((account) => account.accountClass === 'langsiktig')
    .reduce((sum, account) => sum + Math.abs(account.bookedBalance), 0));
  const unclassified = loanRound(bookedTotal - bookedShortTerm - bookedLongTerm);

  const withPlan = rows.filter((row) => row.nextTwelveMonths !== null);
  const nextTwelveTotal = withPlan.length
    ? loanRound(withPlan.reduce((sum, row) => sum + row.nextTwelveMonths, 0))
    : null;
  const documentedNextTwelve = loanRound(withPlan
    .filter((row) => row.verified)
    .reduce((sum, row) => sum + row.nextTwelveMonths, 0));
  // Mulig klassifiseringsavvik: planene sier at mer forfaller det neste året enn
  // det som står som kortsiktig gjeld. FORELØPIG — avdragsfrihet, brudd på
  // lånevilkår og refinansiering etter balansedagen endrer svaret uten at planen
  // viser det, og derfor konkluderer modellen ikke.
  const classificationGap = nextTwelveTotal === null
    ? null
    : loanRound(Math.max(0, nextTwelveTotal - bookedShortTerm));

  const accruedInterestTotal = loanRound(rows.reduce((sum, row) => sum + num(row.accruedInterest), 0));
  const feesTotal = loanRound(rows.reduce((sum, row) => sum + num(row.fees), 0));
  const foreignCurrency = rows.filter((row) => String(row.currency || 'NOK').toUpperCase() !== 'NOK');
  const missingDocumentation = rows.filter((row) => !row.verified);
  const missingPlan = rows.filter((row) => row.needsPlan);

  const ledgerLines = range && window.buildLedgerLines
    ? window.buildLedgerLines({
      from: range.from,
      to: range.to,
      accountCodes: accountRows.map((account) => account.accountId),
      state,
    })
    : [];

  const checks = [
    { key: 'uregistrert', label: t('rcn.check.unregistered', 'Gjeldskontoer uten registrert lån'), value: unregistered.length },
    { key: 'dokumentasjon', label: t('rcn.check.documentation', 'Lån uten gyldig dokumentasjon for perioden'), value: missingDocumentation.length },
    { key: 'plan', label: t('rcn.check.plan', 'Lån uten nedbetalingsplan'), value: missingPlan.length },
    { key: 'renter', label: t('rcn.check.interest', 'Lån med påløpte renter som ikke er bokført'), value: rows.filter((row) => num(row.accruedInterest) > 0.005).length },
    { key: 'gebyr', label: t('rcn.check.fees', 'Lån med gebyrer som ikke er bokført'), value: rows.filter((row) => num(row.fees) > 0.005).length },
    { key: 'valuta', label: t('rcn.check.currency', 'Lån i utenlandsk valuta'), value: foreignCurrency.length },
    { key: 'uklassifisert', label: t('rcn.check.unclassified', 'Bokført gjeld utenfor kort- og langsiktig gjeld'), value: Math.abs(unclassified) > 0.005 ? 1 : 0 },
  ];

  return {
    periodId,
    from: range ? range.from : '',
    to: periodTo || '',
    country,
    rows,
    accounts: accountRows,
    unregistered,
    ledgerLines,
    checks,
    counts: {
      loans: rows.length,
      verified: rows.filter((row) => row.verified).length,
      missingDocumentation: missingDocumentation.length,
      missingPlan: missingPlan.length,
      unregistered: unregistered.length,
      foreignCurrency: foreignCurrency.length,
      sharedAccounts: rows.filter((row) => row.sharesAccount).length,
    },
    summary: {
      bookedTotal,
      openingTotal,
      change: loanRound(bookedTotal - openingTotal),
      verifiedBooked,
      verifiedDocumented,
      preliminaryDifference,
      explained,
      unexplainedDifference,
      unverifiedBooked,
      bookedShortTerm,
      bookedLongTerm,
      unclassified,
      nextTwelveTotal,
      documentedNextTwelve,
      classificationGap,
      accruedInterestTotal,
      feesTotal,
      openItems: loanRound(accruedInterestTotal + feesTotal),
    },
  };
};

Object.assign(window, {
  buildLoanReconciliationView,
  LOAN_DOC_STATUS, LOAN_DOC_ISSUES, LOAN_DOCUMENT_KINDS, LOAN_ITEM_CLASSES,
  LOAN_REPAYMENT_TYPE_OPTIONS,
  loanDocumentationStatus, loanCarryForwardBalance, loanAccountClass,
});
